import React, { FC, PropsWithChildren, ReactNode } from 'react'

import { useParams } from 'react-router-dom'
import { styled } from 'styled-components'

import { PendingEventHandler } from 'core/components/lib/Button/Button'
import Clickable from 'core/components/lib/Clickable'
import ConfirmDialog from 'core/components/lib/ConfirmDialog'
import PermissionDeniedDialog from 'core/components/lib/PermissionDeniedDialog/PermissionDeniedDialog'
import { R } from 'core/helpers'
import { useHasPermission, useBorrowerUrl } from 'core/hooks'
import { Permission } from 'core/types'

import { useFormDisabled } from 'loan/components/actionCards/shared/DisabledContext'

import CardSection from './CardSection'

type BackButtonProps = PropsWithChildren<{
  color?: string
  onClick?: () => void
}>

export const BackButton: FC<BackButtonProps> = ({ children = 'Back', onClick }) => {
  const { loanId } = useParams<{ loanId: string }>()
  const url = useBorrowerUrl(loanId ? `/loans/${loanId}/activity` : '/loans')

  return (
    <Clickable variant='ghost' {...(onClick ? { onClick } : { to: url })}>
      {children}
    </Clickable>
  )
}

type CardSectionSubmitProps = {
  submitText?: string
  cancelText?: string
  onCancel?: () => void
  onSubmit?: PendingEventHandler<React.MouseEvent<HTMLButtonElement, MouseEvent>>
  children?: ReactNode
  disabled?: boolean
  loading?: boolean
  success?: boolean
  skipConfirm?: boolean
  permissions?: Permission | Array<Permission>
}

const CardSectionSubmit: FC<CardSectionSubmitProps> = ({
  submitText = 'Submit',
  cancelText,
  onCancel,
  onSubmit = R.noop,
  children,
  disabled = false,
  loading = false,
  success = false,
  skipConfirm,
  permissions,
}) => {
  const isDisabled = useFormDisabled(disabled)

  const hasPermission = useHasPermission(permissions)

  const common = {
    children: loading ? 'Processing' : submitText,
    disabled: isDisabled || loading,
    variant: 'secondary' as const,
  }

  return (
    <CardSection>
      <SubmitButtonsContainer>
        {!success && <BackButton onClick={onCancel}>{cancelText}</BackButton>}

        {children ??
          (!hasPermission ?
            <PermissionDeniedDialog title={submitText}>
              <Clickable {...common} />
            </PermissionDeniedDialog>
          : success ? <BackButton>Done</BackButton>
          : skipConfirm ? <Clickable type='submit' {...common} />
          : <ConfirmDialog
              content={`Are you sure you want to ${submitText.toLowerCase()}?`}
              onConfirm={onSubmit}
              title={submitText}
            >
              <Clickable {...common} />
            </ConfirmDialog>)}
      </SubmitButtonsContainer>
    </CardSection>
  )
}

export default CardSectionSubmit

export const SubmitButtonsContainer = styled.div<{ margin?: string }>`
  display: flex;
  flex-direction: row;
  gap: 32px;
  align-items: center;
  justify-content: flex-end;
  margin: ${(p) => p.margin ?? '16px 0 0 0'};
`
